<script setup lang="ts">
import { useSumsubSdk } from '@bs2-bet/kyc/composables';

const { $kyc } = useNuxtApp();
const { fetchUserProfile, userLanguage, userCountry } = useUserData();
const { launchWebSdk } = useSumsubSdk($kyc);

const props = defineProps<{ token: string }>();

const sdkScript = ref<Promise<void> | null>(null);
const isInitialized = ref(false);

async function initializeKycFlow() {
  if (!props.token || isInitialized.value) return;

  await fetchUserProfile();

  await launchWebSdk({
    token: props.token,
    lang: userLanguage.value,
    country: userCountry.value,
    getNewAccessToken: () => Promise.resolve(props.token),
  });

  isInitialized.value = true;
}

const injectWebSdkScript = (): Promise<void> => {
  if (window.snsWebSdk?.init) {
    return Promise.resolve();
  }

  if (sdkScript.value) {
    return sdkScript.value;
  }

  sdkScript.value = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');

    script.src = 'https://in.sumsub.com/idensic/static/sns-websdk-builder.js';
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error('Failed to load Sumsub SDK script'));
    document.head.appendChild(script);
  });

  return sdkScript.value;
};

watch(
  () => [props.token],
  async ([token]) => {
    if (token && !isInitialized.value) {
      await injectWebSdkScript();
      await initializeKycFlow();
    }
  },
  { immediate: true },
);

onActivated(async () => {
  if (!isInitialized.value) {
    await initializeKycFlow();
  }
});

onUnmounted(() => {
  isInitialized.value = false;
  document.querySelector('#sumsub-websdk-container')?.replaceChildren();
});
</script>

<template>
  <div
    id="sumsub-websdk-container"
    :class="$style.sdkContainer"
  ></div>
</template>

<style module lang="scss">
.sdkContainer {
  @apply min-h-[100dvh] md:min-h-[690px] max-w-full;
}
</style>
